import {isFeatureEnabled} from '@github-ui/feature-flags'
import {prefetchIssue} from '@github-ui/issue-viewer/IssueViewerLoader'
import type {ViewNavState} from '@github-ui/issue-viewer/OptionConfig'
import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {useLocation} from '@github-ui/react-router'
import {useCallback, useMemo, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {useLoadMoreNavItems} from './use-load-more-nav-items'

type ViewNavRouterState = ViewNavState & {
  searchQuery?: string
}

/**
 * Manages the view navigation state for j/k keyboard navigation between issues.
 * Reads nav context from React Router state (set at list click time),
 * tracks the current item list, and handles loading additional pages.
 */
export function useViewNav() {
  const environment = useRelayEnvironment()
  const location = useLocation()
  const tabbedTimeline = isFeatureEnabled('issues_react_timeline_tabs')

  // Extract nav state passed via React Router from the list view
  const routerNavState =
    (
      location.state as {
        viewNav?: ViewNavRouterState
      } | null
    )?.viewNav ?? null

  // Local copy of items that grows as more pages are loaded
  const [navItems, setNavItems] = useState<ItemIdentifier[]>(routerNavState?.items ?? [])
  const [backItemCount, setBackItemCount] = useState(routerNavState?.backItemCount ?? 0)
  const {loadMore, loadBack} = useLoadMoreNavItems()

  const searchQuery = routerNavState?.searchQuery ?? ''

  // Merge the router state with the current (potentially expanded) items list
  const viewNavState = useMemo(
    () => (routerNavState ? {...routerNavState, items: navItems, backItemCount} : null),
    [routerNavState, navItems, backItemCount],
  )

  // Fetches items from the previous page and prepends them
  const onLoadBack = useCallback(async () => {
    if (backItemCount <= 0 || !searchQuery) return
    const prevItems = await loadBack(searchQuery, backItemCount)
    if (prevItems.length > 0) {
      const existingKeys = new Set(navItems.map(i => `${i.owner}/${i.repo}/${i.number}`))
      const dedupedItems = prevItems.filter(i => !existingKeys.has(`${i.owner}/${i.repo}/${i.number}`))
      if (dedupedItems.length > 0) {
        setNavItems(prev => [...dedupedItems, ...prev])
        setBackItemCount(count => count - dedupedItems.length)
        for (const item of dedupedItems) {
          void prefetchIssue(environment, item.owner, item.repo, item.number, {
            tabbedTimeline,
          })
        }
      }
    }
  }, [loadBack, backItemCount, navItems, searchQuery, environment, tabbedTimeline])

  // Fetches the next page of issues and deduplicates against existing items
  const onLoadMore = useCallback(async () => {
    const totalCount = routerNavState?.totalCount ?? 0
    if (!searchQuery || navItems.length >= totalCount) {
      return
    }
    const result = await loadMore({currentItemCount: navItems.length, totalCount, searchQuery})
    if (result.items.length > 0) {
      const existingKeys = new Set(navItems.map(i => `${i.owner}/${i.repo}/${i.number}`))
      const dedupedItems = result.items.filter(i => !existingKeys.has(`${i.owner}/${i.repo}/${i.number}`))
      if (dedupedItems.length > 0) {
        setNavItems(prev => [...prev, ...dedupedItems])
        // Preheat the new issues for instant navigation
        for (const item of dedupedItems) {
          void prefetchIssue(environment, item.owner, item.repo, item.number, {
            tabbedTimeline,
          })
        }
      }
    }
  }, [loadMore, navItems, routerNavState?.totalCount, searchQuery, environment, tabbedTimeline])

  return {viewNavState, onLoadMore, onLoadBack}
}
